import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as BarTooltip,
} from 'recharts';
import styles from './DataQualityBarChart.styles';
import {
  parseValidDates,
  formatMonthYearLabel,
  tooltipLabelFormatter,
  fallbackRangeLabel,
  buildQualitySeries,
} from '../../utils/dataQualityBar.helpers';
import { InlineLegend, CHART_COLORS, LABEL_MAP } from '../../utils/dataQuality.helpers';

export default function DataQualityBarChart({ data = [], dateRange = null }) {
  const normalizeRange = (r) => {
    if (!r) return null;
    const startRaw = r.start || r.from || '';
    const endRaw = r.end   || r.to   || '';
    const safe = (v) => {
      if (!v) return '';
      const dt = (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) ? new Date(`${v}T00:00:00Z`) : new Date(v);
      if (isNaN(dt)) return '';
      const iso = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()))
        .toISOString()
        .slice(0, 10);
      return iso;
    };
    const s = safe(startRaw);
    const e = safe(endRaw);
    if (!s && !e) return null;
    return { start: s || e, end: e || s };
  };

  const effectiveRange = useMemo(
    () => normalizeRange(dateRange),
    [dateRange]
  );

  const parsedDates = useMemo(() => parseValidDates(data), [data]);

  const monthYearLabel = useMemo(() => {
    const lbl = formatMonthYearLabel(parsedDates);
    return lbl || fallbackRangeLabel(effectiveRange);
  }, [parsedDates, effectiveRange]);

  const barData = useMemo(
    () => buildQualitySeries(data, effectiveRange),
    [data, effectiveRange]
  );

  const manyPoints = barData.length > 16;

  const barLegendEntries = [
    { label: LABEL_MAP.Missing_Type7, color: CHART_COLORS[0] },
    { label: LABEL_MAP.Actual, color: CHART_COLORS[1] },
    { label: LABEL_MAP.Missing_Type9, color: CHART_COLORS[2] },
    { label: LABEL_MAP.Uncertain, color: CHART_COLORS[3] },
    { label: LABEL_MAP.PendingMissing, color: CHART_COLORS[4] },
  ];

  return (
    <Box sx={{ ...styles.card, ...styles.chartRoot, ...styles.chartText }}>
      <Box sx={styles.headerRow}>
        <Typography variant="subtitle2" sx={styles.headerTitle}>Daily Quality</Typography>
        <InlineLegend entries={barLegendEntries} styles={styles} />
      </Box>

      <Box sx={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={barData}
            margin={{ top: 6, right: 12, left: 6, bottom: 10 }}
            barCategoryGap="18%"
            maxBarSize={72}
          >
            <XAxis
              dataKey="day"
              interval={0}
              minTickGap={4}
              height={manyPoints ? 26 : 20}
              tick={{ fontSize: 10, dy: 4 }}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 10 }}
              width={30}
            />
            <BarTooltip
              labelFormatter={tooltipLabelFormatter}
              formatter={(v, name, props) => {
                const display = props?.payload?.__display;
                const intPct = display && Number.isFinite(display[name]) ? display[name] : Math.round(v);
                return [`${intPct}%`, name];
              }}
              wrapperStyle={{ fontSize: 10, padding: '4px 8px' }}
            />
            <Bar dataKey="Missing_Type7" stackId="a" fill={CHART_COLORS[0]} name={LABEL_MAP.Missing_Type7} />
            <Bar dataKey="Actual" stackId="a" fill={CHART_COLORS[1]} name={LABEL_MAP.Actual} />
            <Bar dataKey="Missing_Type9" stackId="a" fill={CHART_COLORS[2]} name={LABEL_MAP.Missing_Type9} />
            <Bar dataKey="Uncertain" stackId="a" fill={CHART_COLORS[3]} name={LABEL_MAP.Uncertain} />
            <Bar dataKey="PendingMissing" stackId="a" fill={CHART_COLORS[4]} name={LABEL_MAP.PendingMissing} />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={styles.footerRow}>
        <Typography variant="caption" sx={styles.footerText}>{monthYearLabel}</Typography>
      </Box>
    </Box>
  );
}
